import React from 'react';
import { InstallBucket, InstallLineItem } from '../../types/bidding';
import { formatCurrency } from '../../utils/bidCalculator';
import './BucketEditor.css';

interface InstallBucketEditorProps {
  bucket: InstallBucket;
  onUpdate: (bucket: InstallBucket) => void;
  readOnly?: boolean;
}

const InstallBucketEditor: React.FC<InstallBucketEditorProps> = ({
  bucket,
  onUpdate,
  readOnly = false,
}) => {
  const handleUpdateLineItem = (index: number, updates: Partial<InstallLineItem>) => {
    const updatedItems = [...bucket.lineItems];
    const item = { ...updatedItems[index], ...updates };
    
    // Recalculate cost
    item.cost = item.rate * item.quantity;
    if (!('billableTotal' in updates)) {
      item.billableTotal = item.cost;
    }
    
    updatedItems[index] = item;
    
    const total = updatedItems.reduce((sum, item) => sum + item.billableTotal, 0);
    onUpdate({ ...bucket, lineItems: updatedItems, total });
  };

  const handleUpdateInstallDays = (days: number) => {
    // Update install days and adjust any daily-rate items
    const updatedItems = bucket.lineItems.map(item => {
      if (item.rateUnit === 'day') {
        const cost = item.rate * days;
        return { ...item, quantity: days, cost, billableTotal: cost };
      }
      return item;
    });
    
    const total = updatedItems.reduce((sum, item) => sum + item.billableTotal, 0);
    onUpdate({ ...bucket, totalInstallDays: days, lineItems: updatedItems, total });
  };

  const handleAddLineItem = () => {
    const newItem: InstallLineItem = {
      id: `install-${Date.now()}`,
      type: 'labor',
      description: '',
      rate: 0,
      rateUnit: 'day',
      quantity: bucket.totalInstallDays,
      cost: 0,
      billableTotal: 0,
    };
    
    const updatedItems = [...bucket.lineItems, newItem];
    onUpdate({ ...bucket, lineItems: updatedItems });
  };

  const handleDeleteLineItem = (index: number) => {
    const updatedItems = bucket.lineItems.filter((_, i) => i !== index);
    const total = updatedItems.reduce((sum, item) => sum + item.billableTotal, 0);
    onUpdate({ ...bucket, lineItems: updatedItems, total });
  };

  // Group items by type for display
  const laborItems = bucket.lineItems.filter(item => item.type === 'labor');
  const equipmentItems = bucket.lineItems.filter(item => item.type === 'equipment');
  const overheadItems = bucket.lineItems.filter(item => item.type === 'overhead');

  const renderLineItems = (items: InstallLineItem[], typeLabel: string) => {
    if (items.length === 0) return null;
    
    return (
      <>
        <tr className="type-header-row">
          <td colSpan={readOnly ? 6 : 7} className="type-header-cell">{typeLabel}</td>
        </tr>
        {items.map((item) => {
          const index = bucket.lineItems.findIndex(i => i.id === item.id);
          return (
            <tr key={item.id}>
              <td>
                {readOnly ? (
                  <span>{item.description}</span>
                ) : (
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleUpdateLineItem(index, { description: e.target.value })}
                    placeholder="Description"
                  />
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>${item.rate}</span>
                ) : (
                  <input
                    type="number"
                    value={item.rate}
                    onChange={(e) => handleUpdateLineItem(index, { rate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="1"
                  />
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>/{item.rateUnit}</span>
                ) : (
                  <select
                    value={item.rateUnit}
                    onChange={(e) => {
                      const newUnit = e.target.value as 'hour' | 'day';
                      const newQty = newUnit === 'day' ? bucket.totalInstallDays : item.quantity;
                      handleUpdateLineItem(index, { rateUnit: newUnit, quantity: newQty });
                    }}
                  >
                    <option value="hour">/hr</option>
                    <option value="day">/day</option>
                  </select>
                )}
              </td>
              <td>
                {readOnly ? (
                  <span>{item.quantity}</span>
                ) : (
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleUpdateLineItem(index, { quantity: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.5"
                    disabled={item.rateUnit === 'day'} // Auto-adjusted by install days
                    title={item.rateUnit === 'day' ? 'Auto-adjusted by Total Install Days' : ''}
                  />
                )}
              </td>
              <td className="calculated-cell">
                {formatCurrency(item.cost)}
              </td>
              <td>
                {readOnly ? (
                  <span>{formatCurrency(item.billableTotal)}</span>
                ) : (
                  <input
                    type="number"
                    value={item.billableTotal}
                    onChange={(e) => handleUpdateLineItem(index, { billableTotal: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                  />
                )}
              </td>
              {!readOnly && (
                <td>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteLineItem(index)}
                    title="Delete line"
                  >
                    ×
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </>
    );
  };

  return (
    <div className="bucket-editor install-bucket-editor">
      <div className="bucket-header">
        <h4>Installation</h4>
        <div className="install-days-control">
          <label>
            Total Install Days:
            {readOnly ? (
              <span className="days-display">{bucket.totalInstallDays}</span>
            ) : (
              <input
                type="number"
                value={bucket.totalInstallDays}
                onChange={(e) => handleUpdateInstallDays(parseFloat(e.target.value) || 1)}
                min="0.5"
                step="0.5"
                className="days-input"
              />
            )}
          </label>
        </div>
        <div className="bucket-total">{formatCurrency(bucket.total)}</div>
      </div>

      <p className="install-note">
        Items with daily rates automatically adjust when Total Install Days changes.
      </p>

      <table className="bucket-table install-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Rate</th>
            <th>Unit</th>
            <th>Qty</th>
            <th>Cost</th>
            <th>Billable</th>
            {!readOnly && <th></th>}
          </tr>
        </thead>
        <tbody>
          {renderLineItems(laborItems, 'Labor')}
          {renderLineItems(equipmentItems, 'Equipment Rentals')}
          {renderLineItems(overheadItems, 'Overhead')}
        </tbody>
      </table>

      {!readOnly && (
        <div className="install-actions">
          <button className="add-line-btn" onClick={handleAddLineItem}>
            + Add Line Item
          </button>
          <select 
            className="quick-add-select"
            onChange={(e) => {
              if (!e.target.value) return;
              const [type, description, rate, unit] = e.target.value.split('|');
              const newItem: InstallLineItem = {
                id: `install-${Date.now()}`,
                type: type as 'labor' | 'equipment' | 'overhead',
                description,
                rate: parseFloat(rate),
                rateUnit: unit as 'hour' | 'day',
                quantity: unit === 'day' ? bucket.totalInstallDays : 10,
                cost: parseFloat(rate) * (unit === 'day' ? bucket.totalInstallDays : 10),
                billableTotal: parseFloat(rate) * (unit === 'day' ? bucket.totalInstallDays : 10),
              };
              onUpdate({ ...bucket, lineItems: [...bucket.lineItems, newItem] });
              e.target.value = '';
            }}
          >
            <option value="">Quick Add...</option>
            <optgroup label="Labor">
              <option value="labor|Crew Lead|500|day">Crew Lead ($500/day)</option>
              <option value="labor|Carpenter|400|day">Carpenter ($400/day)</option>
              <option value="labor|Helper|300|day">Helper ($300/day)</option>
            </optgroup>
            <optgroup label="Equipment">
              <option value="equipment|Crane Hire|1600|day">Crane Hire ($1600/day)</option>
              <option value="equipment|Telehandler Rental|500|day">Telehandler Rental ($500/day)</option>
              <option value="equipment|Scissor Lift Rental|350|day">Scissor Lift Rental ($350/day)</option>
              <option value="equipment|Boom Lift Rental|450|day">Boom Lift Rental ($450/day)</option>
            </optgroup>
            <optgroup label="Overhead">
              <option value="overhead|Travel & Per Diem|300|day">Travel & Per Diem ($300/day)</option>
              <option value="overhead|Site Overhead|200|day">Site Overhead ($200/day)</option>
            </optgroup>
          </select>
        </div>
      )}
    </div>
  );
};

export default InstallBucketEditor;
